import type { ModuleStudioRepository } from '../data/ModuleStudioRepository';
import { GENERATOR_PLATFORM_COLUMNS, type DesignModuleSpecJson, type SimulationModel, type SimulationSummary } from '../data/contracts';

/** E2E-FIX1 (G) — recette réelle « fais la v33 » de Finances (DIAG-E2E-FIN, cause 1) : la simulation chargée était une génération d'un
 * modèle ancien ; « Relier » mesurait des coïncidences. Une simulation ne sert de mesure que si :
 * - ses lignes viennent du prototype (origin « prototype »), et du prototype VIVANT du dossier (empreinte comparée après normalisation
 *   des fins de ligne : un même HTML réimporté en CRLF reste le même) ;
 * - elle a été capturée sur le modèle courant : chaque objet et chaque champ stocké du modèle (hors liens, pièces jointes, calculés) y
 *   figure.
 * Générique : aucun module, objet ni champ nommés. */
export type SimulationFitness = 'live' | 'notLive' | 'olderModel';

/** SHA-256 hexadécimal du texte après normalisation des fins de ligne (CRLF, CR → LF), comme le serveur (PrototypeSampleService). */
export async function normalizedSha256(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text.replace(/\r\n?/g, '\n'));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export type LivePrototype = { normalized: Set<string>; raw: Set<string>; importId: string | null };
/** Empreintes des sources HTML de l'import vivant (le plus récent importé ou analysé) ; vide sans import vivant. */
export async function livePrototype(repository: ModuleStudioRepository, dossier: string): Promise<LivePrototype> {
  const out: LivePrototype = { normalized: new Set(), raw: new Set(), importId: null };
  if (typeof repository.listPrototypeImports !== 'function' || typeof repository.getPrototypeImport !== 'function') return out;
  const live = (await repository.listPrototypeImports(dossier)).find(item => item.status === 'imported' || item.status === 'analysed');
  if (!live) return out;
  out.importId = live.id;
  const source = await repository.getPrototypeImport(live.id);
  for (const piece of source.pieces.filter(item => item.kind === 'html')) {
    if (piece.sha256) out.raw.add(piece.sha256);
    if (typeof repository.getPrototypePieceText === 'function') out.normalized.add(await normalizedSha256(await repository.getPrototypePieceText(live.id, piece.id)));
  }
  return out;
}

type SpecEntity = { kind?: string; fields?: { name: string; type?: string; computed?: unknown; mode?: string; storage?: unknown }[] };
// CM-UNION2 (GAP-CMR-8) : mêmes règles de stockage que le générateur (GeneratorColumns.resolveField, colonnes de plate-forme) :
// un champ « display », un champ « hidden » ou « entityref » sans stockage déclaré, ou une colonne de plate-forme déclarée (tenant_id,
// workspace_id…) n'est jamais une propriété de la simulation ; sans cette règle, toute simulation du Change Management (initiative.tenant_id,
// evaluationgroupe.suggestion) et de la CMDB (room.polygon, importjob.mapping…) était « modèle antérieur ».
const UNSTORED = new Set(['references', 'attachments', 'display']);
const UNSTORED_WITHOUT_STORAGE = new Set(['hidden', 'entityref']);
const PLATFORM = new Set(Object.keys(GENERATOR_PLATFORM_COLUMNS));
const stored = (field: NonNullable<SpecEntity['fields']>[number]) => !field.computed && !UNSTORED.has(field.type ?? '')
  && !(UNSTORED_WITHOUT_STORAGE.has(field.type ?? '') && !field.storage) && !PLATFORM.has(field.name);
/** Les objets et champs stockés du modèle courant absents du modèle de la simulation (vide : même modèle de données). */
export function modelGaps(spec: DesignModuleSpecJson, model: Pick<SimulationModel, 'entities'> | undefined): string[] {
  const captured = new Map((model?.entities ?? []).map(entity => [entity.code, new Set((entity.properties ?? []).map(property => property.field))]));
  const gaps: string[] = [];
  for (const [code, entity] of Object.entries((spec.entities ?? {}) as Record<string, SpecEntity>)) {
    if (code.includes(':') || entity.kind === 'core') continue;
    const fields = captured.get(code);
    if (!fields) { gaps.push(code); continue; }
    for (const field of entity.fields ?? []) if (stored(field) && !fields.has(field.name)) gaps.push(code + '.' + field.name);
  }
  return gaps;
}

/** Ce que vaut une simulation comme mesure pour le modèle courant et le prototype vivant. */
export function simulationFitness(summary: Pick<SimulationSummary, 'origin' | 'prototypeSha256' | 'prototypeRawSha256'>, model: Pick<SimulationModel, 'entities'> | undefined,
  spec: DesignModuleSpecJson, prototype: LivePrototype): SimulationFitness {
  // An older simulation only knows its raw fingerprint : equal to the live normalized one, its source was already LF-only (same text).
  const known = [summary.prototypeSha256, summary.prototypeRawSha256].filter((value): value is string => !!value);
  const fromLive = summary.origin === 'prototype' && known.some(sha => prototype.normalized.has(sha) || prototype.raw.has(sha));
  if (!fromLive) return 'notLive';
  return modelGaps(spec, model).length ? 'olderModel' : 'live';
}
